import { b2x } from '@b2x/react/src';
import React from 'react';

import { useAppContext } from '../AppContext';
import { Container } from '../Container';
import { iconsNames } from '../Icon';
import { Page } from './Page';

export const themeColors = ['primary', 'secondary'] as const;
export type ThemeColor = (typeof themeColors)[number];

export const TemplatePage = () => {
  const page = b2x.usePage({
    populate: {
      content: false,
    },
  });

  const { getBrandVariant } = useAppContext();

  return (
    <Page thingsToLoadBeforePageReady={[page]}>
      <TemplateSection title="Speech Recognition">
        <b2x.template.SpeechRecognition />
      </TemplateSection>

      <TemplateSection title="Grid System">
        <b2x.template.GridSystem />
      </TemplateSection>

      <TemplateSection title="Typography">
        <b2x.template.Typography
          variants={[
            // { className: 'display-1', name: 'Display 1' },
            // { className: 'display-2', name: 'Display 2' },
            // { className: 'display-3', name: 'Display 3' },
            // { className: 'display-4', name: 'Display 4' },
            // { className: 'display-5', name: 'Display 5' },
            // { className: 'display-6', name: 'Display 6' },
            { className: 'h1', name: 'Heading 1' },
            { className: 'h2', name: 'Heading 2' },
            { className: 'h3', name: 'Heading 3' },
            { className: 'h4', name: 'Heading 4' },
            { className: 'h5', name: 'Heading 5' },
            { className: 'h6', name: 'Heading 6' },
            { className: 'fw-bold', name: 'Bold text' },
            { className: 'fw-normal', name: 'Normal text' },
            { className: 'fw-semi-bold', name: 'Semibold text' },
            { className: 'small', name: 'Small text' },
            { className: 'extra-small ', name: 'Extra small text' },
            // { className: 'extra-small', name: 'Extra small text' },
          ]}
        />
      </TemplateSection>

      <TemplateSection title="Icons">
        <b2x.template.Icons names={iconsNames} />
      </TemplateSection>

      <TemplateSection title="Spacing">
        <b2x.template.Spacing />
      </TemplateSection>

      <TemplateSection title="Colors - Only for cta">
        <b2x.template.Colors
          colors={[
            { background: 'primary', text: 'black' },
            { background: 'primary-hover', text: 'black' },
            { background: 'secondary', text: 'white' },
            { background: 'secondary-hover', text: 'white' },
          ]}
        />
      </TemplateSection>

      <TemplateSection title="Colors - Brand">
        <b2x.template.Colors
          colors={[
            { background: 'brand-primary', text: 'white' },
            { background: 'brand-secondary', text: 'black' },
          ]}
        />
      </TemplateSection>

      <TemplateSection title="Colors - Other">
        <b2x.template.Colors
          colors={getBrandVariant({
            goldbet: [
              { background: 'light-blue', text: 'white' },
              { background: 'green', text: 'white' },
              { background: 'orange-intralot', text: 'white' },
              { background: 'hover-yellow', text: 'black' },
            ],
            lottomatica: [
              { background: 'green', text: 'black' },
              { background: 'better-green', text: 'black' },
              { background: 'better-dark-green', text: 'white' },
              { background: 'better-light-green', text: 'black' },
              { background: 'light-blue', text: 'black' },
            ],
            lottomaticaservizi: [{ background: 'green', text: 'black' }],
            planetwin365: [{ background: 'green', text: 'black' }],
          })}
        />
      </TemplateSection>

      <TemplateSection title="Colors - Neutral">
        <b2x.template.Colors
          colors={[
            { background: 'gray-700', text: 'white' },
            { background: 'gray-500', text: 'black' },
            { background: 'gray-300', text: 'black' },
            { background: 'gray-100', text: 'black' },
          ]}
        />
      </TemplateSection>

      <TemplateSection title="Form">
        <Container>
          <b2x.ExampleForm />
        </Container>
      </TemplateSection>

      <TemplateSection title="Buttons">
        <b2x.template.Buttons
          backgrounds={['white']}
          iconsOrLabels={['onlyLabel', 'onlyIcon', 'IconLeftAndLabel', 'IconRightAndLabel']}
          sizes={[undefined]}
          variants={[
            'primary',
            'secondary',
            'brand-primary',
            'brand-secondary',
            'gray-700',
            'gray-500',
            'gray-300',
            'gray-100',
          ]}
        />
      </TemplateSection>

      <TemplateSection title="Dropdowns">
        <Container>
          <div className="row">
            <b2x.template.Dropdowns
              hideAlignments
              hideAutoClose
              hideComponent
              hideDarkVariant
              hideDirections
              hideOffsets
              sizes={[undefined]}
              variants={['primary', 'secondary']}
            />
          </div>
        </Container>
      </TemplateSection>

      <TemplateSection title="Table">
        <Container>
          <b2x.Table border="light" className="border" colNumber={10} responsive xAlignment="center">
            <b2x.TableHead variant="light">
              <tr>
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((th) => (
                  <th key={th}>th{th}</th>
                ))}
              </tr>
            </b2x.TableHead>
            <b2x.TableBody>
              {[1, 2, 3, 4, 5].map((tr) => (
                <tr key={tr}>
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((td) => (
                    <td key={td}>
                      tr{tr} td{td}
                    </td>
                  ))}
                </tr>
              ))}
            </b2x.TableBody>
          </b2x.Table>
        </Container>
      </TemplateSection>

      <TemplateSection title="Tabs">
        <Container>
          <b2x.template.Tabs />
        </Container>
      </TemplateSection>

      <TemplateSection title="Accordion">
        <Container>
          <b2x.Accordion id="AccordionExample">
            <b2x.AccordionItem id="One" title="Title ONE">
              <b2x.LoremIpsum />
            </b2x.AccordionItem>
            <b2x.AccordionItem id="Two" title="Title Two">
              <b2x.LoremIpsum />
            </b2x.AccordionItem>
          </b2x.Accordion>
        </Container>
      </TemplateSection>

      <TemplateSection title="Pagination">
        <Container>
          <b2x.template.Paginations />
        </Container>
      </TemplateSection>
    </Page>
  );
};

interface TemplateSectionProps {
  children?: React.ReactNode;
  title?: string;
}

const TemplateSection = ({ children, title }: TemplateSectionProps) => {
  return (
    <section className="my-5">
      {title && (
        <Container>
          <div className="row">
            <div className="col-12">
              <h1 className="mb-4">{title}</h1>
            </div>
          </div>
        </Container>
      )}
      {children}
    </section>
  );
};
